import * as Fingerprint from 'fingerprintjs2';
import queryString from 'query-string';
import { loadGtm, pushDataLayer } from './gtm';
import initMetrika, { dispatchCall } from './metrika';
import sleep from '../utils/sleep';
import sentryStore from '../utils/sentryStore';
import { SessionIdManager } from './sessionIdManager';

const debug = require('debug')('app:tracking');

const manager = new SessionIdManager();

const HAS_SUBMITTED_METRICS = 'hmes';
const LAST_SOURCE = 'lsrc';

// eslint-disable-next-line import/no-mutable-exports
export let sessionId: string;

export type FraudEvent = {
  initialVisitDate: string;
  isTouchDevice?: boolean;
  isUsingIncognito?: boolean;
  isTryingToHideIdentityScore?: number;

  userAgent: string;
  osType: string;
  browserName: string;
  timezoneOffset: number;
  screenResolution: [number, number];
  hardwareConcurrency: number;
  gpuVendor: string;

  webrtcIps: string[];
  fp: any;

  // 90% precise
  browserFingerprintPrecise: string;
};

function findIP(onNewIP) {
  const pc = new (window as any).RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  });
  const noop = () => {};
  const localIPs = {};
  const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/g;

  function ipIterate(ip) {
    if (!localIPs[ip]) onNewIP(ip);
    localIPs[ip] = true;
  }

  pc.createDataChannel('');

  pc.createOffer((sdp) => {
    sdp.sdp.split('\n').forEach((line) => {
      if (line.indexOf('candidate') < 0) return;
      line.match(ipRegex).forEach(ipIterate);
    });
    pc.setLocalDescription(sdp, noop, noop);
  }, noop);

  pc.onicecandidate = (ice) => {
    if (
      !ice
      || !ice.candidate
      || !ice.candidate.candidate
      || !ice.candidate.candidate.match(ipRegex)
    ) return;
    ice.candidate.candidate.match(ipRegex).forEach(ipIterate);
  };
}

async function findRtcIps(): Promise<string[]> {
  return new Promise((resolve) => {
    try {
      const ips = [];

      findIP((ip) => {
        ips.push(ip);

        if (ips.length === 2) {
          resolve(ips);
        }
      });

      setTimeout(() => resolve(ips), 200);
    } catch (e) {
      resolve([]);
    }
  });
}

const isIncognito = () => new Promise<boolean>((resolve) => {
  const anyWindow = window as any;
  const fs = anyWindow.RequestFileSystem || anyWindow.webkitRequestFileSystem;
  if (!fs) resolve(false);
  else fs(anyWindow.TEMPORARY, 100, () => resolve(false), () => resolve(true));
});

const preciseConfig = {
  excludes: {
    // Unreliable on Windows, see https://github.com/Valve/fingerprintjs2/issues/375
    enumerateDevices: true,
    // devicePixelRatio depends on browser zoom, and it's impossible to detect browser zoom
    pixelRatio: true,
    // DNT depends on incognito mode for some browsers (Chrome)
    doNotTrack: true,
    // uses js fonts already
    fontsFlash: true,
  },
};
const lessPrecise = {
  excludes: {
    ...preciseConfig.excludes,
    availableScreenResolution: true,
    userAgent: true,
    adBlock: true,
  },
};

const totalScores = 7;

export function calculateScore(cp: Fingerprint.Component[]): number {
  let total = 0;
  const mapped: any = {};

  cp.forEach((d) => {
    mapped[d.key] = d.value;
  });

  ['deviceMemory', 'cpuClass', 'plugins', 'webgl', 'audio'].forEach((d) => {
    if (mapped[d] === 'not available') {
      total += 1;
    }
  });

  if (mapped.fonts.length === 0) {
    total += 1;
  }

  total += 1;

  return total / totalScores;
}

export function calculateHash(obj: Fingerprint.Component[]): string {
  return Fingerprint.x64hash128(obj.map((d) => String(d.value)).join(''), 31);
}

export async function nullIfFailed<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch (e) {
    return null;
  }
}

export async function collectMetrics(): Promise<FraudEvent> {
  const precise = await Fingerprint.getPromise(preciseConfig);

  const rtcIps = await nullIfFailed(findRtcIps());
  const hiddenScore = calculateScore(precise);
  const incognito = await nullIfFailed(isIncognito());

  return {
    initialVisitDate: new Date().toISOString(),
    isTouchDevice: 'ontouchstart' in document.documentElement,
    isUsingIncognito: incognito,
    isTryingToHideIdentityScore: hiddenScore,

    userAgent: navigator.userAgent,
    osType: navigator.platform,
    browserName: null,
    timezoneOffset: new Date().getTimezoneOffset(),
    screenResolution: [window.outerWidth, window.outerHeight],
    hardwareConcurrency: navigator.hardwareConcurrency,
    gpuVendor: precise.find((d) => d.key === 'webglVendorAndRenderer').value,

    webrtcIps: rtcIps,
    fp: precise,

    // 90% precise
    browserFingerprintPrecise: calculateHash(precise),
  };
}

// return session id and creates initial events
export function getOrCreateSessionId(): string {
  return manager.getSessionId();
}

export function getSource() {
  try {
    const args = queryString.parse(document.location.search.slice(1));
    args.referer = document.referrer;
    args.sessionDate = new Date().toISOString();

    debug('Source args: ', args);
    return args;
  } catch (e) {
    return {};
  }
}

export function getLastSource() {
  try {
    return JSON.parse(localStorage.getItem(LAST_SOURCE));
  } catch (e) {
    return null;
  }
}

// Source changes only when referrer or source is different from that which was specified earlier
export async function handleSource() {
  const lastSource = getLastSource();
  const source = getSource();

  // When it was stated, we override anyway
  if (source.source) {
    localStorage.setItem(LAST_SOURCE, JSON.stringify(source));
  } else if (
    document.referrer !== ''
    && !document.referrer.includes(document.location.host)
    && !document.referrer.includes('youthink.io')
  ) {
    localStorage.setItem(LAST_SOURCE, JSON.stringify(source));
  } else if (!lastSource) {
    localStorage.setItem(LAST_SOURCE, JSON.stringify(source));
  }

  return source;
}

export async function recordEvent(type: string, data: any) {
  debug('Submitting event %o', { type, data });
  await window.fetch('https://tracker.youthink.io', {
    method: 'POST',
    body: JSON.stringify({
      sessionId: getOrCreateSessionId(),
      eventType: type,
      eventDetails: JSON.stringify(data),
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function submitFingerprintIfNecessary() {
  if (!localStorage.getItem(HAS_SUBMITTED_METRICS)) {
    sleep(1000)
      .then(() => collectMetrics())
      .then(async (metrics) => {
        debug(
          'Collected metrics, gonna submit it to server for further analysis: %O',
          metrics,
        );

        await recordEvent('initialSession', metrics);
        localStorage.setItem(HAS_SUBMITTED_METRICS, 'true');
      })
      .catch((e) => {
        sentryStore.onceReady((sentry) => {
          sentry.captureException(e);
        });
      });
  }
}

export async function initializeTracking() {
  const lastState = localStorage.getItem(LAST_SOURCE);
  handleSource().then(() => {
    if (localStorage.getItem(LAST_SOURCE) !== lastState) {
      debug('Source has been changed, submitting metrics to server');
      recordEvent('newSourceVisit', getLastSource());
    }
  });

  await loadGtm();

  pushDataLayer({ sessionId: getOrCreateSessionId() });
}
